import { PureComponent, ReactNode } from 'react'
import * as cn from 'classnames'
import { SecondaryLine } from 'interfaces/chart'
import * as styles from 'components/features/pfm/MonthlyBarChart/MonthlyBarChart.css'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { doElementsVerticallyIntersect } from 'helpers/styleHelpers'
import { shallowEqual } from 'tangle-shared'
import * as debounce from 'lodash/debounce'

export type MonthlyBarChartLinesProps = {
  limitLine?: SecondaryLine
  compareLine?: SecondaryLine
  isMobile?: boolean
  getOffsetTop: (value: number) => number
}

type LineType = 'comparison' | 'limit'

type MonthlyBarChartLinesState = {
  areLabelsIntersected: boolean
  upperLabel: LineType | null
  compareLine?: SecondaryLine
  limitLine?: SecondaryLine
}

const DEFAULT_STATE: MonthlyBarChartLinesState = {
  areLabelsIntersected: false,
  upperLabel: null,
}

const getStateByProps = (props: MonthlyBarChartLinesProps) => ({
  ...DEFAULT_STATE,
  compareLine: props.compareLine,
  limitLine: props.limitLine,
})

class MonthlyBarChartLines extends PureComponent<MonthlyBarChartLinesProps, MonthlyBarChartLinesState> {
  static getDerivedStateFromProps (nextProps: MonthlyBarChartLinesProps, prevState: MonthlyBarChartLinesState) {
    // discard the intersection-related state
    if (
      !shallowEqual(prevState.compareLine, nextProps.compareLine)
      || !shallowEqual(prevState.limitLine, nextProps.limitLine)
    ) {
      return getStateByProps(nextProps)
    }

    return {}
  }

  state = getStateByProps(this.props)

  private limitLabelsNodes: Partial<Record<LineType, HTMLElement | null>> = {}

  render () {
    const {
      limitLine,
      compareLine,
      getOffsetTop,
    } = this.props

    return (
      <div className={ styles.linesContainer }>
        { limitLine && this.renderLine('limit', limitLine.amount, limitLine.title, getOffsetTop) }
        { compareLine && this.renderLine('comparison', compareLine.amount, compareLine.title, getOffsetTop) }
      </div>
    )
  }

  renderLine (type: LineType, value: number, label: ReactNode, getOffsetTop: (value: number) => number) {
    const { areLabelsIntersected, upperLabel } = this.state
    const { isMobile } = this.props

    const lineClassName = cn(styles.lineWrapper, {
      [styles.minorLineWrapper]: type === 'comparison',
    })

    const labelClassName = cn(styles.limitLabel, {
      [styles.limitLabelMovedUp]: areLabelsIntersected && type === upperLabel,
      [styles.limitLabelMovedDown]: areLabelsIntersected && type !== upperLabel,
    })

    const lineTop = getOffsetTop(value)
    const lineTopStyle = `${ lineTop }%`

    return (
      <div className={ styles.lineContainer }>
        <span className={ lineClassName } style={{ top: lineTopStyle }} />
        <div
          ref={ (el) => this.limitLabelsNodes[type] = el }
          style={{ top: lineTopStyle }}
          className={ labelClassName }
        >
          { !isMobile && this.renderLineLabel(value, label) }
        </div>
      </div>
    )
  }

  renderLineLabel (value: number, label: ReactNode) {
    return (
      <>
        { label }
        <span className={ styles.limitValue }>
          { formatUSCurrency(value) }
        </span>
      </>
    )
  }

  componentDidMount () {
    this.adjustLimitLabels()
    window.addEventListener('resize', this.handleResize)
  }

  componentDidUpdate (prevProps: MonthlyBarChartLinesProps) {
    if (
      !shallowEqual(this.props.compareLine, prevProps.compareLine)
      || !shallowEqual(this.props.limitLine, prevProps.limitLine)
    ) {
      this.adjustLimitLabels()
    }
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.handleResize)
    this.handleResize.cancel()
  }

  private adjustLimitLabels = () => {
    const { comparison, limit } = this.limitLabelsNodes
    const { compareLine, limitLine } = this.props
    if (!comparison || !limit || !compareLine || !limitLine) {
      return
    }

    const areLabelsIntersected = doElementsVerticallyIntersect(comparison, limit)
    if (areLabelsIntersected) {
      const upperLabel = compareLine.amount > limitLine.amount ? 'comparison' : 'limit'
      this.setState({
        areLabelsIntersected,
        upperLabel,
      })
    }
  }

  private handleResize = debounce(this.adjustLimitLabels, 50)
}

export default MonthlyBarChartLines
